import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { BetPageType, DisplaySection, MarketPage, RoutePage } from '@/interface/enum';
import { useZone } from '@/libraries/composables/useZone';
import { useProductPlatformStore } from '@/stores/productPlatformStore';

const displaySectionMap: Record<DisplaySection, BetPageType> = {
    [DisplaySection.Unknown]: BetPageType.Unknown,
    [DisplaySection.TournamentCenter_FeaturedMatches]: BetPageType.TournamentCenter_FeaturedMatches,
    [DisplaySection.TournamentCenter_Groups]: BetPageType.TournamentCenter_Groups,
    [DisplaySection.TournamentCenter_Knockout]: BetPageType.TournamentCenter_Knockout,
    [DisplaySection.TournamentCenter_UpcomingMatches]: BetPageType.TournamentCenter_UpcomingMatches,
    [DisplaySection.TournamentCenter_Outright]: BetPageType.TournamentCenter_Outright,
    [DisplaySection.MpCombination]: BetPageType.MpCombination,
    [DisplaySection.ParlayMode]: BetPageType.MixParlay,
    [DisplaySection.TournamentCenter_Country]: BetPageType.TournamentCenter_Country,
    [DisplaySection.TournamentCenter_Matches]: BetPageType.TournamentCenter_Matches,
    [DisplaySection.TournamentCenter_Prediction]: BetPageType.TournamentCenter_Prediction,
};

export function useBetPage(marketPage: MarketPage, displaySection: DisplaySection = DisplaySection.Unknown) {
    const { isComponentInPp } = storeToRefs(useProductPlatformStore());

    const {
        isInEFootballZone,
        isInMultipleSportsZone,
        isInSportsCupZone,
        isInSimulationSportsZone,
        isInLeagueZone,
        isInTournamentCenterMatches,
        isInTournamentCenterCountries,
    } = useZone();

    const route = useRoute();
    const betPage = computed(() => {
        const routeName = route.name;

        if (isComponentInPp.value) {
            return BetPageType.ProductPlatform;
        }
        if (routeName === RoutePage.MyMatches) {
            return BetPageType.MyMatches;
        }
        if (routeName === RoutePage.MatchDetail) {
            return BetPageType.MatchDetail;
        }
        if (isInSportsCupZone.value) {
            return BetPageType.SportsCup;
        }

        if (isInEFootballZone.value) {
            return BetPageType.EFootball;
        }

        if (isInMultipleSportsZone.value) {
            return BetPageType.MultipleSportsZone;
        }

        if (isInSimulationSportsZone.value) {
            return BetPageType.SimulationSportsZone;
        }

        if (isInLeagueZone.value) {
            return BetPageType.LeagueZone;
        }

        if (isInTournamentCenterMatches.value) {
            return BetPageType.TournamentCenter_Matches;
        }
        if (isInTournamentCenterCountries.value) {
            return BetPageType.TournamentCenter_Country;
        }

        if (displaySection !== DisplaySection.Unknown) {
            return displaySectionMap[displaySection];
        }

        switch (marketPage) {
            case MarketPage.Live:
                return BetPageType.Live;
            case MarketPage.Today:
                return BetPageType.Today;
            case MarketPage.EarlyMarket:
            case MarketPage.EarlyMarketPlus1:
            case MarketPage.EarlyMarketPlus2:
            case MarketPage.EarlyMarketPlus3:
            case MarketPage.EarlyMarketPlus4Over:
                return BetPageType.EarlyMarket;
            default:
                return BetPageType.Unknown;
        }
    });

    return {
        betPage,
    };
}

export function useOutrightBetPage(displaySection: DisplaySection = DisplaySection.Unknown) {
    const { isInSportsCupZone, isInEFootballZone, isInMultipleSportsZone, isInLeagueZone, isInTournamentCenterMatches } = useZone();

    const betPage = computed(() => {
        if (isInSportsCupZone.value) {
            return BetPageType.SportsCup;
        }

        if (isInEFootballZone.value) {
            return BetPageType.EFootball;
        }

        if (isInMultipleSportsZone.value) {
            return BetPageType.MultipleSportsZone;
        }

        if (isInLeagueZone.value) {
            return BetPageType.LeagueZone;
        }

        if (isInTournamentCenterMatches.value) {
            return BetPageType.TournamentCenter_Matches;
        }

        if (displaySection !== DisplaySection.Unknown) {
            return displaySectionMap[displaySection];
        }

        return BetPageType.Outright;
    });

    return {
        betPage,
    };
}

export function useParlayBetPage(openFrom: DisplaySection, isComboParlay: boolean) {
    const betPage = computed(() => {
        const isFromMpCombination = openFrom === DisplaySection.MpCombination;
        const isFromParlayMode = openFrom === DisplaySection.ParlayMode;

        if (!isFromMpCombination && !isFromParlayMode) {
            return BetPageType.Unknown;
        }

        if (isComboParlay) {
            return isFromMpCombination ? BetPageType.MpCombination_ComboParlay : BetPageType.ComboParlay;
        }

        return displaySectionMap[openFrom];
    });

    return {
        betPage,
    };
}
